import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiGet, post } from "../services";

type SearchMode = "basic" | "advanced" | "ai";

/** Backend often returns HTTP 200 with { success: false, status: 401 } — axios would not throw. */
function throwIfApiEnvelopeFailed(data: any) {
  if (data && data.success === false) {
    const status = typeof data.status === "number" ? data.status : 400;
    const err: any = new Error(data.message || "Request failed");
    err.status = status;
    err.response = { status, data };
    throw err;
  }
}

/**
 * apiService rejects failed requests with `error.response` (Axios), so catch() receives
 * `{ status, data, headers, ... }` — body is `err.data`, not `err.response.data`.
 * Also supports a plain AxiosError if thrown elsewhere.
 */
function normalizeHttpReject(err: any): {
  status: number | undefined;
  message: string;
} {
  const status =
    err?.response?.status ?? err?.status ?? err?.statusCode;
  const payload = err?.response?.data ?? err?.data;
  const message =
    (typeof payload?.message === "string" && payload.message) ||
    (typeof payload?.error === "string" && payload.error) ||
    (typeof err?.message === "string" && err.message) ||
    "";
  return { status, message };
}

const DEFAULT_NEARBY_RADIUS_KM = 10;
const ADVANCED_PAGE_SIZE = 20;

const PLACEHOLDER_VENDOR_IMG =
  "https://images.unsplash.com/photo-1519167758481-83f29da8c0f4?w=400&h=240&fit=crop";

const AI_WELCOME_TEXT =
  "Hi — tell me what you need in your own words. For example: “DJ in Lagos for 200 guests under ₦150,000” or “venue and catering in Abuja for a corporate dinner.” I’ll suggest matches below.";

function getStoredUserId(): string | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const candidate = user?.id ?? user?.userId ?? user?.sub;
    if (candidate === undefined || candidate === null) return null;
    return String(candidate);
  } catch {
    return null;
  }
}

function getOrCreateAiSessionId(): string {
  const key = "aiSearchSessionId";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `session_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  localStorage.setItem(key, created);
  return created;
}

function normalizeMode(raw: string | null): SearchMode {
  if (raw === "advanced" || raw === "ai" || raw === "basic") return raw;
  return "basic";
}

const searchModeCards: {
  mode: SearchMode;
  title: string;
  subtitle: string;
  badge?: string;
}[] = [
  {
    mode: "basic",
    title: "Quick search",
    subtitle: "Keywords and city — fast results for browsing.",
  },
  {
    mode: "advanced",
    title: "Advanced search",
    subtitle: "Budget, guests, and location filters for precise matches.",
  },
  {
    mode: "ai",
    title: "AI assistant",
    subtitle: "Describe your event in plain language — we interpret the rest.",
    badge: "Natural language",
  },
];

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  suggested?: string;
  vendors?: any[];
  criteria?: Record<string, unknown>;
};

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const navigateWithSearch = useCallback(
    (params: URLSearchParams) => {
      navigate({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [location.pathname, navigate]
  );

  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const mode = normalizeMode(urlParams.get("mode"));

  const query = urlParams.get("query") || "";
  const selectedLocation = urlParams.get("location") || "";
  const page = urlParams.get("page") || "0";

  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [engagingVendorId, setEngagingVendorId] = useState<number | null>(null);

  /* —— Basic: local form (synced from URL on load / mode change) —— */
  const [formQuery, setFormQuery] = useState(query);
  const [formLocation, setFormLocation] = useState(selectedLocation);

  /* —— Advanced —— */
  const [categories, setCategories] = useState<any[]>([]);
  const [advancedFields, setAdvancedFields] = useState({
    service: "",
    location: "",
    minPrice: "",
    maxPrice: "",
    numberOfGuests: "",
    latitude: "",
    longitude: "",
  });
  const [advancedResults, setAdvancedResults] = useState<any[] | null>(null);
  const [advancedMetadata, setAdvancedMetadata] = useState<any | null>(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedError, setAdvancedError] = useState("");

  /* —— AI chat —— */
  const [aiMessages, setAiMessages] = useState<ChatMessage[]>([]);
  const [aiInput, setAiInput] = useState("");
  const [aiSending, setAiSending] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    setFormQuery(query);
    setFormLocation(selectedLocation);
  }, [query, selectedLocation]);

  useEffect(() => {
    if (mode !== "ai") return;
    setAiMessages((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          id: "welcome",
          role: "assistant" as const,
          text: AI_WELCOME_TEXT,
        },
      ];
    });
  }, [mode]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages, aiSending, mode]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await apiGet("/api/categories");
        throwIfApiEnvelopeFailed(data);
        setCategories(Array.isArray(data?.data) ? data.data : []);
      } catch {
        setCategories([]);
      }
    };
    loadCategories();
  }, []);

  const setMode = useCallback(
    (next: SearchMode) => {
      const p = new URLSearchParams(location.search);
      p.set("mode", next);
      navigateWithSearch(p);
    },
    [location.search, navigateWithSearch]
  );

  useEffect(() => {
    if (mode !== "basic") {
      setLoading(false);
      return;
    }

    async function fetchResults() {
      setLoading(true);
      setError(null);
      try {
        const searchParams = new URLSearchParams();
        if (query) searchParams.append("query", query);
        if (selectedLocation) searchParams.append("location", selectedLocation);
        searchParams.append("page", page);
        searchParams.append("size", "20");
        const data = await apiGet(`/api/search?${searchParams.toString()}`);
        throwIfApiEnvelopeFailed(data);
        if (data && data.data && Array.isArray(data.data.vendors)) {
          setVendors(data.data.vendors);
          setMetadata(data.data.metadata || null);
        } else {
          setVendors([]);
          setMetadata(null);
        }
      } catch (err: any) {
        const { status, message: backendMsg } = normalizeHttpReject(err);
        if (status === 401) {
          setError(
            backendMsg
              ? `${backendMsg} If this continues, sign in again on the same site URL you use for browsing (www vs non-www).`
              : "We could not authorize vendor search. Sign in again on the same site URL (www vs non-www must match), or contact support."
          );
        } else if (status === 403) {
          setError(
            backendMsg ||
              "Vendor search is not available for this account type. It is intended for client accounts; try a client login or contact support if this is unexpected."
          );
        } else {
          setError(
            backendMsg || "Failed to load search results. Please try again later."
          );
        }
        setVendors([]);
        setMetadata(null);
      } finally {
        setLoading(false);
      }
    }
    fetchResults();
  }, [mode, query, selectedLocation, page]);

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("page", newPage.toString());
    navigateWithSearch(newParams);
  };

  const handleBasicSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams(location.search);
    p.set("mode", "basic");
    if (formQuery) p.set("query", formQuery);
    else p.delete("query");
    if (formLocation) p.set("location", formLocation);
    else p.delete("location");
    p.set("page", "0");
    navigateWithSearch(p);
  };

  const handleEngageVendor = async (vendorId: number) => {
    setEngagingVendorId(vendorId);
    try {
      const data = await apiGet(`/api/vendors/${vendorId}/full-details`);
      throwIfApiEnvelopeFailed(data);
      if (data && data.data) {
        navigate(`/business-listing/${vendorId}`, {
          state: { biz: data.data },
        });
      } else {
        alert("Vendor details not found.");
      }
    } catch {
      alert("Failed to load vendor details. Please try again.");
    } finally {
      setEngagingVendorId(null);
    }
  };

  const fetchAdvancedPage = async (pageIndex: number) => {
    setAdvancedLoading(true);
    setAdvancedError("");
    try {
      const params = new URLSearchParams();
      params.append("page", String(pageIndex));
      params.append("size", String(ADVANCED_PAGE_SIZE));
      if (advancedFields.service) params.append("service", advancedFields.service);
      if (advancedFields.location)
        params.append("location", advancedFields.location);
      if (advancedFields.minPrice) params.append("minPrice", advancedFields.minPrice);
      if (advancedFields.maxPrice) params.append("maxPrice", advancedFields.maxPrice);
      if (advancedFields.numberOfGuests)
        params.append("numberOfGuests", advancedFields.numberOfGuests);

      let path = `/api/advanced-search?${params.toString()}`;
      if (advancedFields.latitude && advancedFields.longitude) {
        params.append("latitude", advancedFields.latitude);
        params.append("longitude", advancedFields.longitude);
        params.append("radius", String(DEFAULT_NEARBY_RADIUS_KM));
        if (advancedFields.service) params.set("service", advancedFields.service);
        path = `/api/advanced-search/nearby?${params.toString()}`;
      }

      const data = await apiGet(path);
      throwIfApiEnvelopeFailed(data);
      setAdvancedResults((data?.data && data.data.vendors) || []);
      // totalResults = matching listings (vendor with 2 listings can count twice); aligns with result rows.
      setAdvancedMetadata(data?.data?.metadata ?? null);
    } catch (err: any) {
      const { status } = normalizeHttpReject(err);
      if (status === 401) {
        setAdvancedError("UNAUTHORIZED_401");
      } else if (status === 403) {
        setAdvancedError("FORBIDDEN_403");
      } else {
        setAdvancedError("Search failed. Please try again.");
      }
      setAdvancedMetadata(null);
    } finally {
      setAdvancedLoading(false);
    }
  };

  const handleAdvancedSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdvancedResults(null);
    setAdvancedMetadata(null);
    await fetchAdvancedPage(0);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setAdvancedFields((f) => ({
          ...f,
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }));
      },
      (err) => alert("Failed to get location: " + err.message)
    );
  };

  const sendAiMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = aiInput.trim();
    if (!trimmed || aiSending) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      text: trimmed,
    };
    setAiMessages((prev) => [...prev, userMsg]);
    setAiInput("");
    setAiError(null);
    setAiSending(true);

    try {
      const data = await post("/api/ai-search/chat", {
        message: trimmed,
        sessionId: getOrCreateAiSessionId(),
        userId: getStoredUserId(),
      });
      throwIfApiEnvelopeFailed(data);

      const payload = data?.data;
      const assistantMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: "assistant",
        text: payload?.message || "Here is what I found.",
        suggested: payload?.suggestedResponse || undefined,
        vendors: Array.isArray(payload?.vendors) ? payload.vendors : [],
        criteria: payload?.extractedCriteria || undefined,
      };
      setAiMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const { status } = normalizeHttpReject(err);
      if (status === 401) {
        setAiError("auth");
      } else {
        setAiError("request");
      }
    } finally {
      setAiSending(false);
    }
  };

  const clearAiChat = () => {
    setAiInput("");
    setAiError(null);
    setAiMessages([
      {
        id: `welcome-${Date.now()}`,
        role: "assistant",
        text: AI_WELCOME_TEXT,
      },
    ]);
  };

  const cardBase: React.CSSProperties = {
    flex: "1 1 260px",
    maxWidth: 360,
    textAlign: "left",
    padding: "22px 22px 20px",
    borderRadius: 16,
    border: "2px solid transparent",
    background: "#fff",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(108, 99, 255, 0.08)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };

  const renderVendorGrid = (list: any[], emptyLabel: string) => {
    if (list.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: 40, color: "#666", fontSize: 17 }}>
          {emptyLabel}
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 32,
          justifyContent: "center",
        }}
      >
        {list.map((vendor: any) => (
          <div
            key={vendor.id + (vendor.service || "")}
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 8px #eee",
              padding: 24,
              maxWidth: 340,
              minWidth: 260,
              flex: "1 1 260px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: 480,
            }}
          >
            <img
              src={vendor.image || PLACEHOLDER_VENDOR_IMG}
              alt={vendor.name || "Vendor"}
              style={{
                width: "100%",
                height: 180,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 16,
              }}
            />
            <h4 style={{ color: "#222", fontWeight: 700, marginBottom: 4 }}>
              {vendor.name}
            </h4>
            <div style={{ color: "#888", fontSize: 15, marginBottom: 4 }}>
              {vendor.service}
            </div>
            <div style={{ color: "#666", fontSize: 14, marginBottom: 8 }}>
              {vendor.location}
            </div>
            <div
              style={{
                color: "#444",
                fontSize: 15,
                marginBottom: 12,
                flex: 1,
                width: "100%",
                maxHeight: 160,
                overflowY: "auto",
                lineHeight: 1.5,
              }}
            >
              {vendor.description}
            </div>
            {vendor.priceRange && (
              <div
                style={{ color: "#6C63FF", fontWeight: 600, marginBottom: 8 }}
              >
                Price: {vendor.priceRange.min} - {vendor.priceRange.max}{" "}
                {vendor.priceRange.currency}
              </div>
            )}
            {vendor.isVerified && (
              <div
                style={{
                  color: "#28a745",
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 8,
                }}
              >
                Verified
              </div>
            )}
            <button
              type="button"
              onClick={() => handleEngageVendor(vendor.id)}
              style={{
                background: "#FFD700",
                color: "#222",
                padding: "10px 24px",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 16,
                marginTop: "auto",
                width: "100%",
                border: "none",
                cursor: "pointer",
              }}
              disabled={engagingVendorId === vendor.id}
            >
              {engagingVendorId === vendor.id ? "Loading…" : "Engage vendor"}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8f8ff 0%, #eef0ff 45%, #f8f8ff 100%)",
        paddingBottom: 56,
      }}
    >
      <div className="container" style={{ maxWidth: 1140, paddingTop: 32 }}>
        <div style={{ marginBottom: 28 }}>
          <h1
            style={{
              fontWeight: 800,
              color: "#1a1a2e",
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)",
              marginBottom: 8,
              letterSpacing: "-0.02em",
            }}
          >
            Find vendors
          </h1>
          <p style={{ color: "#5c5c7a", fontSize: 17, lineHeight: 1.55, maxWidth: 640 }}>
            Choose how you want to search — quick keywords, detailed filters, or a
            conversational assistant. Switch anytime.
          </p>
        </div>

        {/* Mode cards */}
        <div
          role="tablist"
          aria-label="Search type"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 20,
            justifyContent: "center",
            marginBottom: 32,
          }}
        >
          {searchModeCards.map((c) => {
            const active = mode === c.mode;
            return (
              <button
                key={c.mode}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMode(c.mode)}
                style={{
                  ...cardBase,
                  borderColor: active ? "#6C63FF" : "rgba(108, 99, 255, 0.15)",
                  boxShadow: active
                    ? "0 12px 32px rgba(108, 99, 255, 0.22)"
                    : cardBase.boxShadow,
                  transform: active ? "translateY(-4px)" : "none",
                }}
              >
                {c.badge && (
                  <span
                    style={{
                      alignSelf: "flex-start",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "#6C63FF",
                      background: "rgba(108, 99, 255, 0.12)",
                      padding: "4px 10px",
                      borderRadius: 999,
                    }}
                  >
                    {c.badge}
                  </span>
                )}
                <div style={{ fontWeight: 800, fontSize: 19, color: "#222" }}>
                  {c.title}
                </div>
                <div style={{ fontSize: 15, color: "#666", lineHeight: 1.5 }}>
                  {c.subtitle}
                </div>
                <span
                  style={{
                    marginTop: 8,
                    fontSize: 14,
                    fontWeight: 700,
                    color: active ? "#6C63FF" : "#999",
                  }}
                >
                  {active ? "Selected" : "Tap to use →"}
                </span>
              </button>
            );
          })}
        </div>

        {/* Content panel */}
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            padding: "28px 24px 36px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
            border: "1px solid rgba(108, 99, 255, 0.12)",
          }}
        >
          {mode === "basic" && (
            <>
              <h2 style={{ fontWeight: 800, color: "#6C63FF", marginBottom: 16 }}>
                Quick search
              </h2>
              <form
                onSubmit={handleBasicSubmit}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 24,
                }}
              >
                <input
                  type="text"
                  placeholder="Search vendors, services…"
                  value={formQuery}
                  onChange={(e) => setFormQuery(e.target.value)}
                  style={{
                    flex: "2 1 220px",
                    border: "1px solid #e8e8f0",
                    padding: "14px 16px",
                    fontSize: 16,
                    borderRadius: 10,
                    outline: "none",
                  }}
                />
                <select
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                  style={{
                    flex: "1 1 160px",
                    border: "1px solid #e8e8f0",
                    padding: "14px 16px",
                    fontSize: 16,
                    borderRadius: 10,
                    background: "#fafaff",
                    color: formLocation ? "#333" : "#888",
                  }}
                >
                  <option value="">All locations</option>
                  <option value="Lagos">Lagos</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Port Harcourt">Port Harcourt</option>
                  <option value="Ibadan">Ibadan</option>
                  <option value="Kano">Kano</option>
                  <option value="Enugu">Enugu</option>
                  <option value="Other">Other</option>
                </select>
                <button
                  type="submit"
                  style={{
                    background: "#6C63FF",
                    color: "#fff",
                    border: "none",
                    padding: "14px 28px",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                >
                  Search
                </button>
              </form>
              <div style={{ marginBottom: 20, color: "#555", fontSize: 15 }}>
                <strong>Query:</strong> {query || <em>Any</em>}
                {" · "}
                <strong>Location:</strong> {selectedLocation || <em>All</em>}
              </div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 48, fontSize: 17 }}>
                  Loading results…
                </div>
              ) : error ? (
                <div style={{ textAlign: "center", color: "#c62828", padding: 40 }}>
                  {error}
                </div>
              ) : (
                renderVendorGrid(vendors, "No vendors found for your search.")
              )}
              {metadata && metadata.totalPages > 1 && (
                <div style={{ marginTop: 32, textAlign: "center" }}>
                  {Array.from({ length: metadata.totalPages }, (_, i) => i).map(
                    (p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handlePageChange(p)}
                        style={{
                          margin: "0 6px",
                          padding: "8px 16px",
                          borderRadius: 8,
                          border:
                            p === Number(page)
                              ? "2px solid #6C63FF"
                              : "1px solid #ccc",
                          background: p === Number(page) ? "#6C63FF" : "#fff",
                          color: p === Number(page) ? "#fff" : "#222",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                        disabled={p === Number(page)}
                      >
                        {p + 1}
                      </button>
                    )
                  )}
                </div>
              )}
            </>
          )}

          {mode === "advanced" && (
            <>
              <h2 style={{ fontWeight: 800, color: "#6C63FF", marginBottom: 16 }}>
                Advanced search
              </h2>
              <form
                onSubmit={handleAdvancedSearch}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 28,
                }}
              >
                <select
                  value={advancedFields.service}
                  onChange={(e) =>
                    setAdvancedFields((f) => ({ ...f, service: e.target.value }))
                  }
                  style={{
                    flex: "2 1 200px",
                    border: "1px solid #e8e8f0",
                    padding: "12px 14px",
                    fontSize: 15,
                    borderRadius: 10,
                    background: "#fafaff",
                  }}
                >
                  <option value="">Service / category</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="City or area"
                  value={advancedFields.location}
                  onChange={(e) =>
                    setAdvancedFields((f) => ({ ...f, location: e.target.value }))
                  }
                  style={{
                    flex: "2 1 180px",
                    border: "1px solid #e8e8f0",
                    padding: "12px 14px",
                    fontSize: 15,
                    borderRadius: 10,
                  }}
                />
                <input
                  type="number"
                  placeholder="Min price"
                  value={advancedFields.minPrice}
                  onChange={(e) =>
                    setAdvancedFields((f) => ({ ...f, minPrice: e.target.value }))
                  }
                  style={{
                    width: 120,
                    border: "1px solid #e8e8f0",
                    padding: "12px 14px",
                    fontSize: 15,
                    borderRadius: 10,
                  }}
                />
                <input
                  type="number"
                  placeholder="Max price"
                  value={advancedFields.maxPrice}
                  onChange={(e) =>
                    setAdvancedFields((f) => ({ ...f, maxPrice: e.target.value }))
                  }
                  style={{
                    width: 120,
                    border: "1px solid #e8e8f0",
                    padding: "12px 14px",
                    fontSize: 15,
                    borderRadius: 10,
                  }}
                />
                <input
                  type="number"
                  placeholder="Guests"
                  value={advancedFields.numberOfGuests}
                  onChange={(e) =>
                    setAdvancedFields((f) => ({
                      ...f,
                      numberOfGuests: e.target.value,
                    }))
                  }
                  style={{
                    width: 120,
                    border: "1px solid #e8e8f0",
                    padding: "12px 14px",
                    fontSize: 15,
                    borderRadius: 10,
                  }}
                />
                <button
                  type="button"
                  onClick={handleUseMyLocation}
                  style={{
                    background: "#f0f0fa",
                    color: "#6C63FF",
                    border: "none",
                    padding: "12px 16px",
                    borderRadius: 10,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Use my location
                </button>
                <button
                  type="submit"
                  disabled={advancedLoading}
                  style={{
                    background: "#6C63FF",
                    color: "#fff",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: 10,
                    fontWeight: 700,
                    cursor: advancedLoading ? "wait" : "pointer",
                    opacity: advancedLoading ? 0.85 : 1,
                  }}
                >
                  {advancedLoading ? "Searching…" : "Search"}
                </button>
              </form>
              {advancedError === "UNAUTHORIZED_401" ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 32,
                    background: "#fff8e1",
                    borderRadius: 12,
                    border: "1px solid #ffc107",
                  }}
                >
                  <p style={{ color: "#6d4c00", marginBottom: 16 }}>
                    Please log in to use advanced search.
                  </p>
                  <a href="/login" style={{ color: "#6C63FF", fontWeight: 700 }}>
                    Go to login
                  </a>
                </div>
              ) : advancedError === "FORBIDDEN_403" ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 32,
                    background: "#fce4ec",
                    borderRadius: 12,
                    border: "1px solid #f48fb1",
                  }}
                >
                  <p style={{ color: "#880e4f", margin: 0, lineHeight: 1.55 }}>
                    Advanced vendor search is not enabled for this account type
                    (for example vendor or admin). Use a client account, or
                    contact support if you need access.
                  </p>
                </div>
              ) : advancedError ? (
                <div style={{ color: "#c62828", textAlign: "center", padding: 24 }}>
                  {advancedError}
                </div>
              ) : advancedResults === null ? (
                <p style={{ color: "#777", textAlign: "center", padding: 32 }}>
                  Set your filters and run a search to see vendors.
                </p>
              ) : (
                <>
                  {advancedMetadata != null &&
                    typeof advancedMetadata.totalResults === "number" && (
                      <div
                        style={{
                          marginBottom: 20,
                          padding: "14px 18px",
                          background: "#f5f6ff",
                          borderRadius: 12,
                          border: "1px solid #e4e6fa",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 15,
                            color: "#2d2d44",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>{advancedMetadata.totalResults}</strong>{" "}
                          matching listing
                          {advancedMetadata.totalResults === 1 ? "" : "s"}
                          {typeof advancedMetadata.totalPages === "number" &&
                            advancedMetadata.totalPages > 0 && (
                              <>
                                {" "}
                                · Page{" "}
                                <strong>
                                  {Number(advancedMetadata.currentPage ?? 0) + 1}
                                </strong>{" "}
                                of{" "}
                                <strong>{advancedMetadata.totalPages}</strong>
                              </>
                            )}
                        </p>
                        <p
                          style={{
                            margin: "10px 0 0",
                            fontSize: 13,
                            color: "#5a5a6e",
                            lineHeight: 1.5,
                          }}
                        >
                          Totals follow listings (not unique vendors): one vendor
                          with several matching listings can add more than one to
                          this count, which matches how result rows are built.
                        </p>
                      </div>
                    )}
                  {renderVendorGrid(
                    advancedResults,
                    "No listings matched those filters."
                  )}
                  {advancedMetadata &&
                    typeof advancedMetadata.totalPages === "number" &&
                    advancedMetadata.totalPages > 1 && (
                      <div style={{ marginTop: 28, textAlign: "center" }}>
                        {Array.from(
                          { length: advancedMetadata.totalPages },
                          (_, i) => i
                        ).map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => fetchAdvancedPage(p)}
                            disabled={
                              advancedLoading ||
                              p ===
                                Number(advancedMetadata.currentPage ?? 0)
                            }
                            style={{
                              margin: "0 6px",
                              padding: "8px 16px",
                              borderRadius: 8,
                              border:
                                p ===
                                Number(advancedMetadata.currentPage ?? 0)
                                  ? "2px solid #6C63FF"
                                  : "1px solid #ccc",
                              background:
                                p ===
                                Number(advancedMetadata.currentPage ?? 0)
                                  ? "#6C63FF"
                                  : "#fff",
                              color:
                                p ===
                                Number(advancedMetadata.currentPage ?? 0)
                                  ? "#fff"
                                  : "#222",
                              fontWeight: 600,
                              cursor: advancedLoading ? "wait" : "pointer",
                              opacity: advancedLoading ? 0.65 : 1,
                            }}
                          >
                            {p + 1}
                          </button>
                        ))}
                      </div>
                    )}
                </>
              )}
            </>
          )}

          {mode === "ai" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <h2 style={{ fontWeight: 800, color: "#6C63FF", margin: 0 }}>
                  AI assistant
                </h2>
                <button
                  type="button"
                  onClick={clearAiChat}
                  style={{
                    background: "transparent",
                    border: "1px solid #ddd",
                    padding: "8px 16px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#555",
                  }}
                >
                  New conversation
                </button>
              </div>

              <div
                style={{
                  border: "1px solid #ececf4",
                  borderRadius: 16,
                  background: "linear-gradient(180deg, #fafbff 0%, #fff 100%)",
                  minHeight: 280,
                  maxHeight: 420,
                  overflowY: "auto",
                  padding: 20,
                  marginBottom: 16,
                }}
              >
                {aiMessages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: "flex",
                      justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "min(92%, 560px)",
                        padding: "14px 18px",
                        borderRadius:
                          m.role === "user"
                            ? "18px 18px 4px 18px"
                            : "18px 18px 18px 4px",
                        background: m.role === "user" ? "#6C63FF" : "#fff",
                        color: m.role === "user" ? "#fff" : "#333",
                        boxShadow:
                          m.role === "user"
                            ? "0 4px 14px rgba(108, 99, 255, 0.35)"
                            : "0 2px 12px rgba(0,0,0,0.06)",
                        border:
                          m.role === "assistant" ? "1px solid #eee" : "none",
                        fontSize: 15,
                        lineHeight: 1.55,
                      }}
                    >
                      {m.text}
                      {m.suggested && (
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 14,
                            fontStyle: "italic",
                            opacity: m.role === "user" ? 0.95 : 0.88,
                          }}
                        >
                          {m.suggested}
                        </div>
                      )}
                      {m.criteria && m.role === "assistant" && (
                        <div
                          style={{
                            marginTop: 12,
                            fontSize: 13,
                            color: "#555",
                          }}
                        >
                          <strong>Understood:</strong>{" "}
                          {Object.entries(m.criteria)
                            .filter(
                              ([, v]) =>
                                v !== null &&
                                v !== undefined &&
                                String(v) !== "" &&
                                String(v) !== "null"
                            )
                            .map(([k, v]) => `${k}: ${String(v)}`)
                            .join(" · ") || "—"}
                        </div>
                      )}
                      {m.vendors &&
                        m.vendors.length > 0 &&
                        m.role === "assistant" && (
                          <div style={{ marginTop: 16 }}>
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: 10,
                                fontSize: 14,
                                color: "#6C63FF",
                              }}
                            >
                              Matching vendors ({m.vendors.length})
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 12,
                                overflowX: "auto",
                                paddingBottom: 8,
                              }}
                            >
                              {m.vendors.map((v: any, idx: number) => (
                                <div
                                  key={v.id ?? idx}
                                  style={{
                                    flex: "0 0 200px",
                                    background: "#f8f8ff",
                                    borderRadius: 12,
                                    padding: 12,
                                    border: "1px solid #e8e8f4",
                                  }}
                                >
                                  <img
                                    src={v.image || PLACEHOLDER_VENDOR_IMG}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: 90,
                                      objectFit: "cover",
                                      borderRadius: 8,
                                      marginBottom: 8,
                                    }}
                                  />
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 14,
                                      color: "#222",
                                    }}
                                  >
                                    {v.name || "Vendor"}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      v.id && handleEngageVendor(Number(v.id))
                                    }
                                    style={{
                                      marginTop: 8,
                                      width: "100%",
                                      background: "#6C63FF",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 8,
                                      padding: "8px 0",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      fontSize: 13,
                                    }}
                                  >
                                    View
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
                {aiSending && (
                  <div style={{ color: "#6C63FF", fontSize: 14, paddingLeft: 4 }}>
                    Assistant is thinking…
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {aiError === "auth" && (
                <div
                  style={{
                    background: "#fff8e1",
                    padding: 16,
                    borderRadius: 12,
                    marginBottom: 12,
                    color: "#6d4c00",
                  }}
                >
                  Please <a href="/login">log in</a> to use the AI assistant.
                </div>
              )}
              {aiError === "request" && (
                <div style={{ color: "#c62828", marginBottom: 12, fontSize: 14 }}>
                  Something went wrong. Please try again.
                </div>
              )}

              <form
                onSubmit={sendAiMessage}
                style={{ display: "flex", gap: 12, flexWrap: "wrap" }}
              >
                <input
                  type="text"
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder="Type what you need… (Enter to send)"
                  style={{
                    flex: "1 1 280px",
                    border: "1px solid #e8e8f0",
                    padding: "14px 18px",
                    fontSize: 16,
                    borderRadius: 12,
                    outline: "none",
                  }}
                  disabled={aiSending}
                />
                <button
                  type="submit"
                  disabled={aiSending || !aiInput.trim()}
                  style={{
                    background: "#6C63FF",
                    color: "#fff",
                    border: "none",
                    padding: "14px 28px",
                    borderRadius: 12,
                    fontWeight: 700,
                    cursor:
                      aiSending || !aiInput.trim() ? "not-allowed" : "pointer",
                    opacity: aiSending || !aiInput.trim() ? 0.6 : 1,
                  }}
                >
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchResults;
